<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatRelativeDate } from '~/lib/dateFormat';
import type { Comment } from '~/types/models';

interface Props {
    entityType: Comment['entityType'];
    entityId: string;
    title?: string;
    placeholder?: string;
    maxItems?: number;
    showHeader?: boolean;
    contained?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    title: 'Comments',
    placeholder: 'Add a comment...',
    maxItems: 100,
    showHeader: true,
    contained: true,
});

const commentsStore = useCommentsStore();
const authStore = useAuthStore();
const userStore = useUserStore();

const draft = ref('');
const submitting = ref(false);
const localError = ref('');

const entityKey = computed(() => `${props.entityType}:${props.entityId}`);
const comments = computed(() => {
    const allComments = commentsStore.commentsByEntity[entityKey.value] || [];
    return allComments.slice(-props.maxItems);
});
const loading = computed(() => commentsStore.loadingByEntity[entityKey.value] || false);
const currentUserId = computed(() => authStore.user?.uid || userStore.user?.id || '');

function getInitials(name?: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts.at(0) || '';
    const last = parts.at(-1) || first;
    if (parts.length === 1) return first.slice(0, 2).toUpperCase();
    return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
}

function canDelete(comment: Comment): boolean {
    return !!currentUserId.value && comment.authorId === currentUserId.value;
}

async function loadComments() {
    if (!props.entityId) return;
    localError.value = '';
    try {
        await commentsStore.fetchComments(props.entityType, props.entityId);
    } catch (error: any) {
        localError.value = error.message || 'Failed to load comments.';
    }
}

async function submitComment() {
    if (!draft.value.trim()) return;
    localError.value = '';
    submitting.value = true;

    try {
        await commentsStore.addComment(props.entityType, props.entityId, draft.value);
        draft.value = '';
    } catch (error: any) {
        localError.value = error.message || 'Failed to post comment.';
    } finally {
        submitting.value = false;
    }
}

async function handleDelete(commentId: string) {
    localError.value = '';
    try {
        await commentsStore.deleteComment(props.entityType, props.entityId, commentId);
    } catch (error: any) {
        localError.value = error.message || 'Failed to delete comment.';
    }
}

watch(() => [props.entityType, props.entityId], () => {
    draft.value = '';
    loadComments();
}, { immediate: true });
</script>

<template>
    <div class="space-y-4">
        <div v-if="showHeader" class="flex items-center justify-between">
            <h3 class="text-base font-semibold">{{ title }}</h3>
            <span class="text-xs text-muted-foreground">{{ comments.length }} total</span>
        </div>

        <div class="space-y-3">
            <Input
                :key="`${entityType}-${entityId}-comment-draft`"
                v-model="draft"
                name="comment-draft"
                autocomplete="off"
                :placeholder="placeholder"
                :disabled="submitting"
                @keyup.enter="submitComment"
            />
            <div class="flex items-center justify-between gap-2">
                <p class="text-xs text-muted-foreground">Press enter to post quickly.</p>
                <Button size="sm" :disabled="!draft.trim() || submitting" @click="submitComment">
                    {{ submitting ? 'Posting...' : 'Post comment' }}
                </Button>
            </div>
        </div>

        <p v-if="localError || commentsStore.commentsError" class="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
            {{ localError || commentsStore.commentsError }}
        </p>

        <div
            v-if="loading"
            :class="[
                'text-sm text-muted-foreground',
                contained ? 'rounded-lg border border-dashed p-4' : 'py-4',
            ]"
        >
            Loading comments...
        </div>

        <div
            v-else-if="comments.length === 0"
            :class="[
                'text-center text-sm text-muted-foreground',
                contained ? 'rounded-lg border border-dashed p-6' : 'py-6',
            ]"
        >
            No comments yet. Start the thread.
        </div>

        <div v-else :class="contained ? 'space-y-3' : 'divide-y divide-border/60'">
            <div
                v-for="comment in comments"
                :key="comment.id"
                :class="contained ? 'rounded-lg border bg-card p-3' : 'py-3 first:pt-0 last:pb-0'"
            >
                <div class="flex items-start gap-3">
                    <div class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-semibold">
                        <img
                            v-if="comment.authorAvatar"
                            :src="comment.authorAvatar"
                            :alt="comment.authorName || 'Comment author avatar'"
                            class="h-full w-full object-cover"
                        >
                        <span v-else>{{ getInitials(comment.authorName) }}</span>
                    </div>

                    <div class="min-w-0 flex-1 space-y-1">
                        <div class="flex flex-wrap items-center justify-between gap-2">
                            <div class="flex items-center gap-2">
                                <p class="text-sm font-medium">{{ comment.authorName || 'Unknown user' }}</p>
                                <p class="text-xs text-muted-foreground">{{ formatRelativeDate(comment.createdAt) }}</p>
                            </div>
                            <Button
                                v-if="canDelete(comment)"
                                variant="ghost"
                                size="sm"
                                class="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                @click="handleDelete(comment.id)"
                            >
                                Delete
                            </Button>
                        </div>
                        <p class="whitespace-pre-wrap text-sm text-foreground">{{ comment.content }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
